import {
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  Component,
  effect,
  inject
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NgxTolgeeModule } from '@tolgee/ngx';

import { Template } from '../../../../../core/template';
import { emulateClick } from '../../core/a11y';
import { Error } from '../../core/error';
import { NotificationService } from '../../notification/notification.service';
import { TelInputComponent } from '../../shared/components/tel-input/tel-input.component';
import { AutoCloseDirective } from '../../shared/directives/auto-close.directive';
import { HoverGradientDirective } from '../../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../../shared/directives/progress-click.directive';
import { Center } from '../../shared/model/center.model';
import { AuthService } from '../../shared/services/auth.service';
import { CenterService } from '../../shared/services/center.service';
import { ContactService } from '../../shared/services/contact.service';
import { GlobalService } from '../../shared/services/globals.service';
import { SelectionService } from '../../shared/services/selection.service';
import { SiteService } from '../../shared/services/site.service';
import { EmailValidator } from '../../shared/validators/validators';

type ContactForm = {
  center: FormControl<Center | null>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  entity: FormControl<string>;
  number: FormControl<number>;
  email: FormControl<string>;
  phone: FormControl<string>;
  address: FormControl<string>;
  zip: FormControl<string>;
  city: FormControl<string>;
  message: FormControl<string>;
  acceptTerms: FormControl<boolean>;
};

@Component({
  selector: 'app-cse-contact',
  imports: [
    NgxTolgeeModule,
    NgClass,
    HoverGradientDirective,
    ReactiveFormsModule,
    ProgressClickDirective,
    TelInputComponent,
    AutoCloseDirective,
    NgOptimizedImage
  ],
  templateUrl: './contact.component.html'
})
export class ContactComponent {
  readonly emulateClick = emulateClick;
  authService = inject(AuthService);
  notificationService = inject(NotificationService);
  siteService = inject(SiteService);
  contactService = inject(ContactService);
  selectionService = inject(SelectionService);
  centerService = inject(CenterService);
  globalService = inject(GlobalService);
  form: FormGroup<ContactForm>;

  constructor() {
    const user = this.authService.user();
    this.form = new FormGroup({
      center: new FormControl(this.selectionService.center(), {
        nonNullable: true,
        validators: [ Validators.required ]
      }),
      firstName: new FormControl(user?.firstName ?? '', { nonNullable: true, validators: [ Validators.required ] }),
      lastName: new FormControl(user?.lastName ?? '', { nonNullable: true, validators: [ Validators.required ] }),
      entity: new FormControl('', { nonNullable: true, validators: [ Validators.required ] }),
      number: new FormControl(1, { nonNullable: true, validators: [ Validators.required, Validators.min(1) ] }),
      email: new FormControl(user?.email ?? '', {
        nonNullable: true,
        validators: [ Validators.required, EmailValidator ]
      }),
      phone: new FormControl(user?.phone ?? '', {
        nonNullable: true, validators: [ Validators.required, Validators.minLength(12) ]
      }),
      address: new FormControl(user?.address ?? '', { nonNullable: true, validators: [ Validators.required ] }),
      zip: new FormControl(user?.zip ?? '', { nonNullable: true, validators: [ Validators.required ] }),
      city: new FormControl(user?.city ?? '', { nonNullable: true, validators: [ Validators.required ] }),
      message: new FormControl('', { nonNullable: true, validators: [ Validators.required ] }),
      acceptTerms: new FormControl(user?.authCompleted ?? false, {
        nonNullable: true,
        validators: [ Validators.requiredTrue ]
      }),
    });
    effect(() => {
      this.form.get('center')?.setValue(this.selectionService.center());
    });
  }

  get acceptTerms(): boolean {
    return this.form.get('acceptTerms')?.value ?? false;
  }

  set acceptTerms(value: boolean) {
    this.form.get('acceptTerms')?.setValue(value);
  }

  /**
   * Select current center
   * @param center The selected center
   */
  selectCenter(center: Center): void {
    this.selectionService.center.set(center);
  }

  submit = async (): Promise<void> => {
    const data = this.form.getRawValue();
    try {
      await this.contactService.contact(
        data.center?.cseContacts ?? this.globalService.settings().contacts,
        Template.CseEmail,
        data);
      this.notificationService.open({
        type: 'success',
        title: 'mailContacted',
        message: 'mailContactedDescription'
      });
      this.form.reset();
    } catch (err) {
      const error = err as Error;
      this.notificationService.open({ type: 'error', title: error.details.code, message: error.message });
    }
  }
}
